import React, { useState, useRef, useContext, lazy, Suspense } from 'react';
import ReactDOM from 'react-dom';
import { VStack, Center } from 'native-base';
import PicnicMarker from "./components/PicnicMarker";
import { useNavigate, useSearchParams } from "react-router-dom";
import AppBar from './components/AppBar';
import { getZonasApi } from "./services/picnicapi.js"
import "./MainPage.css"
import useSupercluster from "use-supercluster"
import { LanguageContext } from './context/LanguageProvider';
import { isMobile } from "./utils"
import { UserLocatorButton } from 'react-google-maps-user-location';
import {Helmet} from "react-helmet"

const AlertBox = lazy(() => import('./components/AlertBox'));
const GoogleMap = lazy(() => import('google-map-react'));

export default function MainPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { i18n } = useContext(LanguageContext)
  const defaultLat = 40.7263401;
  const defaultLng = -1.0268783;
  const defaultZoom = 6;
  const urlLat = parseFloat(searchParams.get('lat'))
  const urlLng = parseFloat(searchParams.get('lng'))
  const urlZoom = parseInt(searchParams.get('z'))
  const [mapState, setMapState] = useState({
    markers: [],
    center: { lat: !isNaN(urlLat) ? urlLat : defaultLat, lng: !isNaN(urlLng) ? urlLng : defaultLng },
    zoom: !isNaN(urlZoom) ? urlZoom : defaultZoom,
    bounds: [],
  });

  const [createAreaIsOpen, setCreateAreaIsOpen] = useState(false);
  const [newAreaData, setNewAreaData] = useState(null);
  const myTimeout = useRef();
  const mapRef = useRef();

  const _onChange = (center, zoom, marginBounds) => {
    if (center && center.lat !== defaultLat && center.lng !== defaultLng) {
      navigate(`?lat=${center.lat}&lng=${center.lng}&z=${zoom}`, false)
      localStorage.setItem("mapRegion", `?lat=${center.lat}&lng=${center.lng}&z=${zoom}`)
    }
    getZonas([
      marginBounds.nw.lat,
      marginBounds.nw.lng,
      marginBounds.se.lat,
      marginBounds.se.lng,
    ], zoom);

    if (mapRef.current !== undefined) {
      if (zoom < 15) {
        mapRef.current.setMapTypeId("roadmap");
      } else if (zoom >= 15) {
        mapRef.current.setMapTypeId("satellite");
      }
    }
  }

  const onLocationError = (errorCode) => {
    if (errorCode === 1){
      alert(i18n.t("error_location_denied"))
    }else{
      alert(i18n.t("error_location"))
    }
  }

  const openCreateNewAreaBox = (e) => {
    setCreateAreaIsOpen(true);
    setNewAreaData({ lat: e.lat(), lng: e.lng() });
  }
  const handleApiLoaded = (map, maps) => {
    mapRef.current = map;
    map.addListener("click", (e) => {
      myTimeout.current = setTimeout(() => openCreateNewAreaBox(e.latLng), 500);
    });
    map.addListener("dblclick", () => {
      clearTimeout(myTimeout.current);
    });

    const controlPosition = document.createElement("div");
    ReactDOM.render(<UserLocatorButton mapRef={mapRef} maps={maps} onError={(e) => onLocationError(e)}/>, controlPosition)
    map.controls[maps.ControlPosition.RIGHT_TOP].push(controlPosition)
  };

  const getZonas = (region, zoom) => {
    setMapState({
      ...mapState, loading: true
    })

    getZonasApi(region).then(function (result) {
      setMapState({
        ...mapState, markers: result, loading: false, zoom: zoom, bounds: [
          region[1], //nw.lng
          region[2], //.se.lat,
          region[3], //.se.lng,
          region[0], //.nw.lat
        ]
      })
    })
  }

  const pushNewAreaPage = () => {
    navigate('/newarea',
      {
        state: newAreaData
      });
  }

  const points = mapState?.markers?.map(crime => ({
    type: "Feature",
    properties: { cluster: false, data: crime, nombre: crime.nombre },
    geometry: {
      type: "Point",
      coordinates: [
        parseFloat(crime.lon),
        parseFloat(crime.lat)
      ]
    }
  }));

  const { clusters, supercluster } = useSupercluster({
    points,
    bounds: mapState.bounds,
    zoom: mapState.zoom,
    options: { radius: 200, maxZoom: 20 }
  });

  const onClusterClick = (data) => {
    clearTimeout(myTimeout.current);
    const expansionZoom = Math.min(
      supercluster.getClusterExpansionZoom(data.properties.cluster_id),
      20
    );
    mapRef.current.setZoom(expansionZoom);
    mapRef.current.panTo({ lat: data.geometry.coordinates[1], lng: data.geometry.coordinates[0] });
  }

  return (
    <>
      <Helmet>
        <title>Picnic app</title>    
        <meta name="description" content={i18n.t("helmet_index_description")} />
        <meta name="robots" content="index, follow"/>
      </Helmet>
      <VStack alignItems="center">
        <AppBar actions={[{ spinner: mapState.loading }]} />
        <div style={{ height: 'calc(100dvh - 60px)', marginTop: "60px", width: '100%' }}>
          <Center>
            <Suspense fallback={null}>
              <AlertBox
                header={i18n.t("nueva_zona_add_btn")}
                body={i18n.t("add_zonas_title")}
                closeButton
                isOpen={createAreaIsOpen}
                onClose={() => setCreateAreaIsOpen(false)}
                textButton1={i18n.t("si")}
                textButton2={i18n.t("no")}
                onPressButton1={() => pushNewAreaPage()}
                onPressButton2={() => setCreateAreaIsOpen(false)} />
            </Suspense>
          </Center>
          <Suspense fallback={null}>
            <GoogleMap
              bootstrapURLKeys={{ key: 'AIzaSyBDieG2tMEHrQeKyTHRwdRBqsiTKI6F3XE' }}
              defaultCenter={mapState.center}
              options={{
                rotateControl: !isMobile(),
                zoomControl: !isMobile(),
                gestureHandling: "greedy",
                fullscreenControl: false,
                scaleControl: false
              }}
              defaultZoom={mapState.zoom}
              yesIWantToUseGoogleMapApiInternals
              onChange={({ center, zoom, marginBounds }) => {
                _onChange(center, zoom, marginBounds);
              }}
              onGoogleApiLoaded={({ map, maps }) => handleApiLoaded(map, maps)}>
              {clusters.map((marker, index) =>
                <PicnicMarker
                  key={index}
                  lat={marker.geometry.coordinates[1]}
                  lng={marker.geometry.coordinates[0]}
                  data={marker}
                  onClusterClick={onClusterClick}
                />
              )}
            </GoogleMap>
          </Suspense>
        </div>
      </VStack>
    </>
  );
}